import { useState, useEffect, useCallback } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { 
  Crown, 
  Calendar, 
  CreditCard, 
  AlertCircle, 
  CheckCircle2,
  RefreshCw,
  X,
  ExternalLink,
  Loader2
} from 'lucide-react';
import { useAuth } from '../lib/auth-context';
import { toast } from 'sonner';
import { projectId, publicAnonKey } from '/utils/supabase/info';
import {
  syncSubscriptionFromServer,
  SUBSCRIPTION_UPDATED_EVENT,
} from '../lib/sync-subscription';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from './ui/alert-dialog';

interface Subscription {
  plan: string;
  status: string;
  currentPeriodEnd?: string;
  cancelAtPeriodEnd?: boolean;
  billingPeriod?: string;
}

export function SubscriptionManagement() {
  const auth = useAuth();
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const loadSubscription = useCallback(() => {
    const plan = localStorage.getItem('tension_subscription') || 'free';
    const subData = localStorage.getItem('tension_subscription_data');

    if (subData) {
      try {
        const parsed = JSON.parse(subData);
        setSubscription(parsed);
      } catch {
        setSubscription({ plan, status: 'active' });
      }
    } else {
      setSubscription({ plan, status: plan === 'free' ? 'none' : 'active' });
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    loadSubscription();
  }, [loadSubscription]);

  useEffect(() => {
    const onUpdate = () => loadSubscription();
    window.addEventListener(SUBSCRIPTION_UPDATED_EVENT, onUpdate);
    return () => window.removeEventListener(SUBSCRIPTION_UPDATED_EVENT, onUpdate);
  }, [loadSubscription]);

  const syncSubscription = async () => {
    if (!auth?.user || !auth?.accessToken) {
      toast.error('Please sign in to sync subscription');
      return;
    }

    setSyncing(true);
    console.log('🔄 Syncing subscription from server...');

    try {
      const result = await syncSubscriptionFromServer(auth.accessToken);

      if (result.ok === false) {
        console.error('❌ Sync failed:', result.error);
        toast.error(result.error || 'Failed to sync subscription');
        return;
      }

      console.log('✅ Subscription synced:', result.subscription);
      if (result.subscription) {
        setSubscription(result.subscription);
        toast.success(
          `Subscription synced! You're on the ${result.subscription.plan.toUpperCase()} plan.`,
        );
      } else {
        toast.info('No active subscription found');
      }
    } catch (error) {
      console.error('❌ Sync error:', error);
      toast.error('Network error. Please check your connection.');
    } finally {
      setSyncing(false);
    }
  };

  const cancelSubscription = async () => {
    if (!auth?.user || !auth?.accessToken) {
      toast.error('Please sign in to manage subscription');
      return;
    }

    setCancelling(true);
    console.log('❌ Cancelling subscription...');

    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-ba6cedc4/cancel-subscription`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${publicAnonKey}`,
            'X-User-Token': auth.accessToken,
          },
        }
      );

      const data = await response.json();

      if (response.ok) {
        console.log('✅ Subscription cancelled');
        
        // Update local state
        if (subscription) {
          const updated = { ...subscription, cancelAtPeriodEnd: true };
          setSubscription(updated);
          localStorage.setItem('tension_subscription_data', JSON.stringify(updated));
        }
        
        toast.success(
          'Subscription cancelled. Premium features will remain active until the end of your billing period.'
        );
      } else {
        console.error('❌ Cancel failed:', data);
        toast.error(data.error || 'Failed to cancel subscription');
      }
    } catch (error) {
      console.error('❌ Cancel error:', error);
      toast.error('Network error. Please check your connection.');
    } finally {
      setCancelling(false);
    }
  };

  const reactivateSubscription = async () => {
    if (!auth?.user || !auth?.accessToken) {
      toast.error('Please sign in to manage subscription');
      return;
    }

    setCancelling(true);
    console.log('✅ Reactivating subscription...');

    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-ba6cedc4/reactivate-subscription`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${publicAnonKey}`,
            'X-User-Token': auth.accessToken,
          },
        }
      );

      const data = await response.json();

      if (response.ok) {
        console.log('✅ Subscription reactivated');
        
        // Update local state
        if (subscription) {
          const updated = { ...subscription, cancelAtPeriodEnd: false };
          setSubscription(updated);
          localStorage.setItem('tension_subscription_data', JSON.stringify(updated));
        }
        
        toast.success('Subscription reactivated! You\'ll continue to be billed.');
      } else {
        console.error('❌ Reactivate failed:', data);
        toast.error(data.error || 'Failed to reactivate subscription');
      }
    } catch (error) {
      console.error('❌ Reactivate error:', error);
      toast.error('Network error. Please check your connection.');
    } finally {
      setCancelling(false);
    }
  };

  if (loading) {
    return (
      <Card className="p-6">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-neutral-400" />
        </div>
      </Card>
    );
  }

  const plan = subscription?.plan || 'free';
  const isPremium = plan === 'premium' || plan === 'pro';
  const isCancelled = subscription?.cancelAtPeriodEnd;

  return (
    <Card className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-medium text-neutral-900">Subscription</h3>
            {isPremium && (
              <Crown className="w-5 h-5 text-amber-600" />
            )}
          </div>
          <p className="text-sm text-neutral-600">
            Manage your TENSION subscription and billing
          </p>
        </div>
        
        <Button
          onClick={syncSubscription}
          variant="outline"
          size="sm"
          disabled={syncing}
        >
          {syncing ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <RefreshCw className="w-4 h-4" />
          )}
          <span className="ml-2">Sync</span>
        </Button>
      </div>

      {/* Current Plan */}
      <div className={`rounded-lg p-6 ${
        plan === 'pro' 
          ? 'bg-gradient-to-br from-amber-50 to-yellow-50 border border-amber-200'
          : plan === 'premium'
          ? 'bg-gradient-to-br from-blue-50 to-violet-50 border border-blue-200'
          : 'bg-neutral-50 border border-neutral-200'
      }`}>
        <div className="space-y-4">
          <div>
            <p className="text-sm text-neutral-600 mb-1">Current Plan</p>
            <div className="flex items-center gap-2">
              <h4 className="text-2xl font-light text-neutral-900 capitalize">{plan}</h4>
              {subscription?.status === 'active' && (
                <CheckCircle2 className="w-5 h-5 text-green-600" />
              )}
            </div>
            {subscription?.billingPeriod && (
              <p className="text-xs text-neutral-600 mt-1">
                Billed {subscription.billingPeriod}
              </p>
            )}
          </div>

          {isPremium && subscription?.currentPeriodEnd && (
            <div className="flex items-start gap-2 text-sm">
              <Calendar className="w-4 h-4 text-neutral-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-neutral-600">
                  {isCancelled ? 'Active until' : 'Renews on'}{' '}
                  <span className="font-medium text-neutral-900">
                    {new Date(subscription.currentPeriodEnd).toLocaleDateString('en-US', {
                      month: 'long',
                      day: 'numeric',
                      year: 'numeric'
                    })}
                  </span>
                </p>
              </div>
            </div>
          )}

          {isCancelled && (
            <div className="tension-glow bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-amber-900">Subscription Cancelled</p>
                <p className="text-xs text-amber-700 mt-0.5">
                  Your premium features will remain active until the end of your billing period.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Features */}
      <div className="space-y-3">
        <h4 className="text-sm font-medium text-neutral-900">
          {plan === 'free' ? 'Free Plan Includes' : 'Your Plan Includes'}
        </h4>
        <div className="space-y-2 text-sm">
          {plan === 'free' && (
            <>
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                <span className="text-neutral-700">Daily tension score check-ins</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                <span className="text-neutral-700">7-day data history</span>
              </div>
              <div className="flex items-start gap-2">
                <X className="w-4 h-4 text-neutral-400 flex-shrink-0 mt-0.5" />
                <span className="text-neutral-500">Advanced forecasting</span>
              </div>
              <div className="flex items-start gap-2">
                <X className="w-4 h-4 text-neutral-400 flex-shrink-0 mt-0.5" />
                <span className="text-neutral-500">Personalized action cards</span>
              </div>
            </>
          )}
          {plan === 'premium' && (
            <>
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                <span className="text-neutral-700">24-72 hour tension forecasting</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                <span className="text-neutral-700">Personalized action cards</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                <span className="text-neutral-700">Unlimited data history</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                <span className="text-neutral-700">Pattern detection & insights</span>
              </div>
            </>
          )}
          {plan === 'pro' && (
            <>
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <span className="text-neutral-700">7-day advanced forecasting</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <span className="text-neutral-700">Multi-relationship tracking (up to 3)</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <span className="text-neutral-700">Advanced ML insights</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <span className="text-neutral-700">1-on-1 coaching session (monthly)</span>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-col gap-3 pt-4 border-t border-neutral-200">
        {plan === 'free' && (
          <Button
            onClick={() => window.location.href = '/pricing'}
            className="w-full"
          >
            <Crown className="w-4 h-4 mr-2" />
            Upgrade to Premium
          </Button>
        )}

        {isPremium && !isCancelled && (
          <>
            <Button
              onClick={() => window.location.href = '/pricing'}
              variant="outline"
              className="w-full"
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              Change Plan
            </Button>

            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" className="w-full text-red-600 hover:text-red-700 hover:bg-red-50">
                  Cancel Subscription
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Cancel Subscription?</AlertDialogTitle>
                  <AlertDialogDescription className="space-y-2">
                    <p>
                      Your subscription will remain active until the end of your current billing period
                      ({subscription?.currentPeriodEnd ? new Date(subscription.currentPeriodEnd).toLocaleDateString() : 'end of period'}).
                    </p>
                    <p className="font-medium text-neutral-900">
                      You'll lose access to:
                    </p>
                    <ul className="text-sm space-y-1 list-disc list-inside">
                      <li>Advanced forecasting</li>
                      <li>Personalized action cards</li>
                      <li>Pattern insights</li>
                      <li>Unlimited history</li>
                    </ul>
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Keep Subscription</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={cancelSubscription}
                    disabled={cancelling}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    {cancelling ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      'Cancel Subscription'
                    )}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </>
        )}

        {isPremium && isCancelled && (
          <Button
            onClick={reactivateSubscription}
            disabled={cancelling}
            className="w-full bg-green-600 hover:bg-green-700"
          >
            {cancelling ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                <CheckCircle2 className="w-4 h-4 mr-2" />
                Reactivate Subscription
              </>
            )}
          </Button>
        )}

        {import.meta.env.VITE_STRIPE_BILLING_URL ? (
          <Button
            variant="ghost"
            className="w-full text-sm"
            onClick={() => window.open(import.meta.env.VITE_STRIPE_BILLING_URL!, '_blank')}
          >
            <CreditCard className="w-4 h-4 mr-2" />
            Manage Billing in Stripe
          </Button>
        ) : (
          <p className="text-xs text-neutral-500 py-2">
            Billing management not configured. Contact support@tension.app for subscription changes.
          </p>
        )}
      </div>

      {/* Help Text */}
      <div className="bg-neutral-50 rounded-lg p-4">
        <p className="text-xs text-neutral-600">
          <strong>Need help?</strong> Contact support at support@tension.app or visit our{' '}
          <a href="/faq" className="text-violet-600 hover:underline">FAQ page</a>
        </p>
      </div>
    </Card>
  );
}